// SCR-01 first-sign-in gates and invite activation (plan 11.4.4, 7.1.4, 7.7; BUILD-SPEC-api §1.2 · §2 auth/me):
// prohibited-use pledge · MFA enrolment · notice consent (+ recording level) · invite lookup/accept. Every body is checked
// against the contract schema. These calls run while the session is still gated, so a 401 is answered on the sign-in
// screen itself (allow401) instead of the app-wide "signed out" handler.
import {
  ConsentView,
  InviteLookupResponse,
  MfaEnrollResponse,
  NoticeView,
  OkResponse,
  PledgeResponse,
  type RecordLevel,
} from '@aiax-hub/contracts';
import type { z } from 'zod';
import { apiFetch } from '../../api/client';

export const GATE_API = {
  pledge: '/api/v1/auth/pledge',
  mfaEnroll: '/api/v1/auth/mfa/enroll',
  mfaVerify: '/api/v1/auth/mfa/enroll/verify',
  invite: '/api/v1/auth/invite',
  consent: '/api/v1/me/consent',
  agree: '/api/v1/me/consent/agree',
  notice: '/api/v1/me/notice',
} as const;

/** Query keys shared with the "my records & consent" screen (same endpoints, same shapes). */
export const gateKeys = {
  pledge: ['auth', 'pledge'] as const,
  consent: ['me', 'consent'] as const,
  notice: ['me', 'notice'] as const,
};

export type Pledge = z.infer<typeof PledgeResponse>;
export type Notice = z.infer<typeof NoticeView>;
export type Consent = z.infer<typeof ConsentView>;
export type MfaSecret = z.infer<typeof MfaEnrollResponse>;
export type InviteLookup = z.infer<typeof InviteLookupResponse>;
export interface InviteAccept {
  code: string;
  loginId: string;
  password: string;
  displayName: string;
}

export const fetchPledge = (signal?: AbortSignal): Promise<Pledge> => apiFetch(GATE_API.pledge, { schema: PledgeResponse, allow401: true, signal });
export const ackPledge = (p: Pick<Pledge, 'version' | 'hash'>, signature: string) =>
  apiFetch(GATE_API.pledge, { method: 'POST', body: { version: p.version, hash: p.hash, signature }, schema: OkResponse, allow401: true });

/** Shows the secret once (no-store); calling it again replaces a secret that was not verified yet. */
export const enrollMfa = (): Promise<MfaSecret> => apiFetch(GATE_API.mfaEnroll, { method: 'POST', schema: MfaEnrollResponse, allow401: true });
export const verifyMfa = (code: string) => apiFetch(GATE_API.mfaVerify, { method: 'POST', body: { code }, schema: OkResponse, allow401: true });

export const fetchNotice = (signal?: AbortSignal): Promise<Notice> => apiFetch(GATE_API.notice, { schema: NoticeView, allow401: true, signal });
export const fetchConsent = (signal?: AbortSignal): Promise<Consent> => apiFetch(GATE_API.consent, { schema: ConsentView, allow401: true, signal });
/** The version and hash of the notice that was shown — a newer published notice answers 409 notice_changed. */
export const agreeNotice = (n: Pick<Notice, 'version' | 'hash'>): Promise<Consent> =>
  apiFetch(GATE_API.agree, { method: 'POST', body: { noticeVersion: n.version, noticeHash: n.hash }, schema: ConsentView, allow401: true });
/** Raising to ④ needs the "secrets may slip into commands" acknowledgement. */
export const chooseLevel = (level: RecordLevel, secretRiskAck = false): Promise<Consent> =>
  apiFetch(GATE_API.consent, { method: 'PUT', body: { level, ...(secretRiskAck ? { secretRiskAck: true } : {}), source: 'settings' }, schema: ConsentView, allow401: true });

export const lookupInvite = (code: string, signal?: AbortSignal): Promise<InviteLookup> =>
  apiFetch(GATE_API.invite, { query: { code }, schema: InviteLookupResponse, allow401: true, signal });
export const acceptInvite = (body: InviteAccept) => apiFetch(GATE_API.invite, { method: 'POST', body, schema: OkResponse, allow401: true });

/** Invite codes look like AIAX-7KQ2-M4XA (letters without I/O, digits without 0/1); typed codes are normalised. */
export const INVITE_CODE = /^AIAX-[A-HJ-NP-Z2-9]{4}-[A-HJ-NP-Z2-9]{4}$/;
export function normalizeInviteCode(raw: string): string {
  const s = raw.trim().toUpperCase().replace(/\s+/g, '');
  const bare = s.replace(/^AIAX-?/, '').replace(/-/g, '');
  return bare.length === 8 ? `AIAX-${bare.slice(0, 4)}-${bare.slice(4)}` : s;
}
/** Login id rule of the account forms (contract SetupRequest / InviteAcceptRequest). */
export const LOGIN_ID = /^[a-zA-Z0-9._-]{3,64}$/;
